import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getAllSGRWarrantyRequests } from 'lib/api/sgr';
import { useState } from 'react';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CreateWarrantyDialog from './components/CreateWarrantyDialog';
import WarrantyDetailDialog from './components/WarrantyDetailDialog';
import TableWithPagination from 'lib/designSystem/TableWithPagination';

const PAGE_SIZE = 10;

const SGR = () => {
  const dispatch = useDispatch();
  const [warranties, setWarranties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState<any>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchWarranties = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setIsLoading(true);
    const query = generateQuery({ page, pageSize });
    try {
      const response = await getAllSGRWarrantyRequests(dispatch, query);
      setWarranties(response.data);
      setPagination(response.pagination);
      return response.data;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((w: any) => w.id === warrantyId);
    if (warranty) {
      setSelectedWarranty(warranty);
      setOpenDetailDialog(true);
    }
  };

  const handleRefreshWarranties = async () => {
    const response = await fetchWarranties({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    if (response && selectedWarranty) {
      const updatedWarranty = response.find(
        (w: any) => w.id === selectedWarranty.id,
      );
      if (updatedWarranty) setSelectedWarranty(updatedWarranty);
    }
  };

  useEffect(() => {
    fetchWarranties({ page: 1, pageSize: PAGE_SIZE });
  }, []);

  const tableHeaders = [
    {
      label: 'ID',
      key: 'id',
      type: 'numeric-id',
    },
    {
      label: 'Tipo',
      key: 'translatedType',
    },
    {
      label: 'Cliente',
      key: 'businessName',
    },
    {
      label: 'Estado',
      key: 'translatedStatus',
      type: 'status',
    },
    {
      label: 'Monto Cotizado',
      key: 'quotedAmount',
      type: 'money',
    },
    {
      label: 'Moneda',
      key: 'quotedCurrency',
    },
    {
      label: 'Fecha Cotización',
      key: 'quoteDate',
      type: 'date',
    },
    {
      label: 'Fecha Inicio',
      key: 'startDate',
      type: 'date',
    },
    {
      label: 'Fecha Término',
      key: 'endDate',
      type: 'date',
    },
  ];

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <>
      <TableWithPagination
        data={warranties}
        setData={setWarranties}
        dataHeaders={tableHeaders}
        buttons={[
          {
            name: 'Nueva Garantía',
            color: 'primary',
            action: () => setOpenCreateDialog(true),
          },
        ]}
        caption="Lista de garantías emitidas y cotizaciones realizadas"
        handleClickRow={handleOpenWarrantyDetail}
        showActions
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchWarranties}
        setLoading={setIsLoading}
        exportToFile="xlsx"
        headerBackgroundColor="transparent"
        lastElement
      />

      <CreateWarrantyDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fetchWarranties={handleRefreshWarranties}
      />

      <WarrantyDetailDialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        warranty={selectedWarranty}
        refreshWarranty={handleRefreshWarranties}
      />
    </>
  );
};

export default SGR;
